import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  Grid,
  Panel,
  PanelSection,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import { HtmlPreview } from '@/components/html-preview';
import { channelValueOf } from '@/features/deliveries';
import type { Delivery, DeliveryDetail } from '@/services/db/delivery/entity';
import { useDeliveryDetailContent } from '@/services/db/delivery/queries';
import { isEmptyString } from '@/utils/string';
import { LiveNotificationMessagePreview } from './live-notification-message-preview';
import { InAppMessagePreview } from './in-app-message-preview';
import { InboxMessagePreview } from './inbox-message-preview';
import { PushActionResults } from './push-action-results';
import { PushMessagePreview } from './push-message-preview';
import { SmsMessagePreview } from './sms-message-preview';
import { WebhookMessagePreview } from './webhook-message-preview';
import { WhatsappMessagePreview } from './whatsapp-message-preview';

type DeliveryMessagePreviewProps = { deliveryId: string; delivery: Delivery };

/**
 * The rendered message content (`getDeliveryDetailContent`), dispatched by
 * channel (Ember `cio-delivery-detail/preview` body). Push renders its
 * device-status results above a Title/Body/Image/Link payload preview. In-app
 * renders a Message (decoded visual-editor HTML) and JSON tab; the JSON-editor
 * composer-rendered visual is deferred (see `InAppMessagePreview`). The in-app /
 * inbox config panels (`InAppConfig` /
 * `InboxConfig`) render in the page's main flow (see `index.tsx`); Ember's
 * two-column aside isn't ported yet.
 *
 * `hide_body` (message retention disabled) blanks email/SMS/WhatsApp/generic
 * entirely; push blanks only its payload (results still render); webhook,
 * in-app, and inbox dispatch ahead of that fallback in Ember and always render.
 *
 * The full `Delivery` is threaded through (not just its `action_type`) because
 * push reads per-device statuses off `meta.reason`.
 */
export function DeliveryMessagePreview({ deliveryId, delivery }: DeliveryMessagePreviewProps) {
  const { data: detail } = useDeliveryDetailContent({ id: deliveryId });

  return (
    <Panel>
      <PanelSection>
        <MessageBody delivery={delivery} detail={detail} />
      </PanelSection>
    </Panel>
  );
}

/** Message-retention-disabled placeholder (Ember `hide_body` blank slate). */
function HiddenBody() {
  return (
    <EmptyState>
      <EmptyStateTitle>Message content is hidden</EmptyStateTitle>
      <EmptyStateDescription>This delivery's content is not available to preview.</EmptyStateDescription>
    </EmptyState>
  );
}

type MessageBodyProps = { delivery: Delivery; detail: DeliveryDetail };

/**
 * Channels with a dedicated preview renderer. `channelValueOf` returns the raw
 * API `action_type` string (an arbitrary API-origin value, not a typed union),
 * so we narrow it to this union with a type guard rather than a cast — a true
 * `never` over the whole channel catalog isn't reachable off a `string`. Email
 * and any action type this list doesn't name fall through to the generic body;
 * the switch's `never` default still forces a new *preview* channel to be wired
 * here at compile time.
 */
const PREVIEW_CHANNELS = [
  'twilio',
  'sms',
  'whatsapp',
  'push',
  'webhook',
  'live_notification',
  'in_app',
  'inbox',
] as const;
type PreviewChannel = (typeof PREVIEW_CHANNELS)[number];
const PREVIEW_CHANNEL_SET: ReadonlySet<string> = new Set(PREVIEW_CHANNELS);

/**
 * Channels Ember blanks entirely when message retention is disabled (SMS /
 * WhatsApp / live-notification). Push is NOT here: its device-status results
 * always render and only the payload preview is hidden — handled inside
 * `PushPreview`.
 */
const HIDE_BODY_GATED_CHANNELS: ReadonlySet<PreviewChannel> = new Set([
  'twilio',
  'sms',
  'whatsapp',
  'live_notification',
]);

function isPreviewChannel(channel: string): channel is PreviewChannel {
  return PREVIEW_CHANNEL_SET.has(channel);
}

function MessageBody({ delivery, detail }: MessageBodyProps) {
  const channel = channelValueOf(delivery.action_type);
  // Ember renders the address rows above the body and outside the retention gate — so the
  // recipient stays visible for SMS/in-app/… and when the body is hidden. Only email carries
  // the Subject/From rows alongside it. Push is the exception: Ember renders the device-status
  // results *instead of* the address table, so it has no To row at all.
  const isGeneric = !isPreviewChannel(channel);

  return (
    <Stack gap="200">
      <MessageAddressLines detail={detail} withSubjectAndFrom={isGeneric} withRecipient={channel !== 'push'} />
      <MessageBodyContent channel={channel} delivery={delivery} detail={detail} />
    </Stack>
  );
}

type MessageAddressLinesProps = { detail: DeliveryDetail; withSubjectAndFrom: boolean; withRecipient: boolean };

type AddressRowProps = { label: string; value: string; isMasked?: boolean };

/** One `label / value` row of Ember's address table: right-aligned bare label, then the value. */
function AddressRow({ label, value, isMasked = false }: AddressRowProps) {
  return (
    <>
      <Text as="div" size="sm" color="subtle" textAlign="right">
        {label}
      </Text>
      <Text as="div" size="sm" className={isMasked ? 'fs-mask' : undefined}>
        {value}
      </Text>
    </>
  );
}

/**
 * Ember's address table — a 2-column `label / value` grid above the body. Every channel with an
 * address branch renders a `To` row; push has no branch (its results panel replaces the table),
 * so it renders none. Only email adds `From` and `Subject`, and Ember's row order is
 * From → To → Subject.
 */
function MessageAddressLines({ detail, withSubjectAndFrom, withRecipient }: MessageAddressLinesProps) {
  const rows = [
    withSubjectAndFrom && !isEmptyString(detail.from) ? { label: 'From', value: detail.from } : null,
    !withRecipient || isEmptyString(detail.recipient) ? null : { label: 'To', value: detail.recipient, isMasked: true },
    withSubjectAndFrom && !isEmptyString(detail.subject)
      ? { label: 'Subject', value: detail.subject, isMasked: true }
      : null,
  ].filter((row) => row != null);

  if (rows.length === 0) return null;

  return (
    <Grid columns="auto 1fr" gap="100" columnGap="200">
      {rows.map((row) => (
        <AddressRow key={row.label} label={row.label} value={row.value} isMasked={row.isMasked} />
      ))}
    </Grid>
  );
}

type MessageBodyContentProps = { channel: string; delivery: Delivery; detail: DeliveryDetail };

/** The body itself, with each channel's retention gating (the address rows render above it). */
function MessageBodyContent({ channel, delivery, detail }: MessageBodyContentProps) {
  if (!isPreviewChannel(channel)) {
    // Email + unknown → generic body, which honors hide_body (Ember `else` branch).
    return detail.hide_body === true ? <HiddenBody /> : <GenericMessageBody detail={detail} />;
  }
  if (HIDE_BODY_GATED_CHANNELS.has(channel) && detail.hide_body === true) {
    return <HiddenBody />;
  }
  return <ChannelPreview channel={channel} delivery={delivery} detail={detail} />;
}

type ChannelPreviewProps = { channel: PreviewChannel; delivery: Delivery; detail: DeliveryDetail };

/** Exhaustive dispatch to each channel's dedicated preview (SMS shares twilio/sms); retention gating is handled in `MessageBody`. */
function ChannelPreview({ channel, delivery, detail }: ChannelPreviewProps) {
  switch (channel) {
    case 'twilio':
    case 'sms':
      return <SmsMessagePreview body={detail.body} />;
    case 'whatsapp':
      return <WhatsappMessagePreview body={detail.body} />;
    case 'live_notification':
      return <LiveNotificationMessagePreview body={detail.body} />;
    case 'push':
      return <PushPreview delivery={delivery} detail={detail} />;
    // Webhook / in-app / inbox always render (Ember dispatches them ahead of the hide_body fallback).
    case 'webhook':
      return <WebhookMessagePreview body={detail.body} />;
    case 'in_app':
      return <InAppMessagePreview body={detail.body} />;
    case 'inbox':
      return <InboxMessagePreview body={detail.body} />;
    default: {
      const _exhaustive: never = channel;
      return _exhaustive;
    }
  }
}

type PushPreviewProps = { delivery: Delivery; detail: DeliveryDetail };

/**
 * Push: device-status results above the Title/Body/Image/Link payload (Ember's
 * panel order). The results always render; only the payload is blanked when
 * message retention is disabled (Ember gates just the `<Previewer>`).
 */
function PushPreview({ delivery, detail }: PushPreviewProps) {
  return (
    <Stack gap="300">
      <PushActionResults delivery={delivery} />
      {detail.hide_body === true ? <HiddenBody /> : <PushMessagePreview delivery={delivery} detail={detail} />}
    </Stack>
  );
}

type GenericMessageBodyProps = { detail: DeliveryDetail };

/** Email + the deferred channels: the sanitized HTML body (address lines render above it). */
function GenericMessageBody({ detail }: GenericMessageBodyProps) {
  return isEmptyString(detail.body) ? (
    <Text color="subtle">No message content.</Text>
  ) : (
    <HtmlPreview body={detail.body} className="fs-mask" />
  );
}
